import { FormikErrors, FormikTouched } from 'formik';
import FormInput from '../../components/forms/FormInput';
import { ChangeEvent, SetStateAction } from 'react';
import { API_URL } from '../../lib/api.ts';
import {
    cn
} from '../../lib/helpers.ts';
import { SchemaDrivenFormFieldProps } from '../../lib/types.ts';
import AutocompleteSchools from '../../components/forms/Autocomplete.tsx';
import { IconAlertCircleFill } from '../../components/Icon.tsx';

export function renderSchemaForm(
    values: object,
    errors: FormikErrors<object>,
    touched: FormikTouched<object>,
    setFieldValue: (
        field: string,
        value: never,
        shouldValidate?: boolean
    ) => Promise<FormikErrors<object>> | Promise<void>,
    setValues: (
        values: SetStateAction<object>,
        shouldValidate?: boolean
    ) => Promise<FormikErrors<object>> | Promise<void>,
    handleChange: {
        (e: ChangeEvent<never>): void;
        <T_1 = string | ChangeEvent<never>>(
            field: T_1
        ): T_1 extends ChangeEvent<never>
            ? void
            : (e: string | ChangeEvent<never>) => void;
    },
    handleBlur?: {
        (e: ChangeEvent<never>): void;
        <T_1 = string | ChangeEvent<never>>(
            field: T_1
        ): T_1 extends ChangeEvent<never>
            ? void
            : (e: string | ChangeEvent<never>) => void;
    }
) {
    const renderForm = ({
        type,
        name,
        label,
        content,
        required,
        placeholder,
        options,
        readonly,
        disabled,
        className,
    }: SchemaDrivenFormFieldProps) => {
        const requiredWithDefault = required ?? true;

        switch (type) {
            case 'heading':
                return (
                    <h2
                        key={name}
                        className={cn(
                            '!mt-12 mb-0 block text-2xl font-bold',
                            className
                        )}
                    >
                        {label}
                    </h2>
                );
            case 'subheading':
                return (
                    <div
                        key={name}
                        className="!mt-0 block text-sm text-ink-800"
                    >
                        {label}
                    </div>
                );
            case 'hidden':
                return null;
            case 'grid':
                return (
                    <div
                        key={name}
                        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
                    >
                        {content?.map(renderForm)}
                    </div>
                );
            case 'html':
                return (
                    <div key={name} className="w-full">
                        {label}
                    </div>
                );
            case 'license':
                return (
                    <iframe
                        key={name}
                        src={`${API_URL}/../files/vsimavec-licencni-smlouva-pro-skoly.pdf`}
                        width="100%"
                        height="400"
                    />
                    // <div className="h-96 w-full overflow-y-scroll rounded-xl border border-cloud-300 bg-cloud-200 px-4 py-3 text-ink-800">
                    //     <License />
                    // </div>
                );
            case 'autocomplete':
                return (
                    <AutocompleteSchools
                        key={name}
                        name={name}
                        label={label!}
                        placeholder="Zkuste vyhledat školu podle názvu, adresy nebo IČO."
                        required={requiredWithDefault}
                        value={values[name as keyof typeof values]}
                        error={
                            errors[name as keyof typeof errors] &&
                            touched[name as keyof typeof touched]
                                ? errors[name as keyof typeof errors]
                                : null
                        }
                        setFieldValue={setFieldValue}
                        setValues={(newValues) => {
                            console.log('combined', {
                                ...values,
                                ...newValues,
                            });
                            setValues({
                                ...values,
                                ...newValues,
                            });
                        }}
                        onBlur={handleBlur}
                        apiToFormNamesMap={{
                            id: 'institution_id',
                            name: 'institution',
                            ico: 'institution_ico',
                            street: 'institution_street',
                            city: 'institution_city',
                            postalCode: 'institution_postal_code',
                        }}
                    />
                );
            case 'radio':
                return (
                    <div className="flex flex-col gap-2" key={name}>
                        {!!label && (
                            <div className="mb-2 flex w-full items-center justify-between">
                                <label
                                    htmlFor={name}
                                    className="block font-medium"
                                >
                                    {label}{' '}
                                    {required && (
                                        <span className="text-red-500">*</span>
                                    )}
                                </label>
                            </div>
                        )}

                        {options!.map((option) => (
                            <div
                                className="flex items-baseline"
                                key={option.value}
                            >
                                <input
                                    type="radio"
                                    name={name}
                                    id={option.value}
                                    className="mr-3 h-4 w-4 translate-y-0.5 rounded border border-cloud-300"
                                    required={requiredWithDefault}
                                    onChange={handleChange}
                                />
                                <label htmlFor={option.value}>
                                    {option.label}
                                    {requiredWithDefault && (
                                        <span className="text-red-500">
                                            &nbsp;*
                                        </span>
                                    )}
                                </label>
                            </div>
                        ))}
                    </div>
                );
            case 'checkbox':
                return (
                    <div className="flex flex-col gap-2" key={name}>
                        <div className="flex items-baseline" key={name}>
                            <input
                                type="checkbox"
                                name={name}
                                id={name}
                                className="mr-3 h-4 w-4 translate-y-0.5 rounded border border-cloud-300"
                                required={requiredWithDefault}
                                onChange={handleChange}
                            />
                            <label htmlFor={name}>
                                {label}
                                {requiredWithDefault && (
                                    <span className="text-red-500">
                                        &nbsp;*
                                    </span>
                                )}
                            </label>
                        </div>
                        {errors[name as keyof typeof errors] &&
                            touched[name as keyof typeof touched] && (
                                <div className="mt-2 flex space-x-2.5 font-medium text-red-500">
                                    <IconAlertCircleFill
                                        size={20}
                                        className="shrink-0"
                                    />
                                    <p>{errors[name as keyof typeof errors]}</p>
                                </div>
                            )}
                    </div>
                );
            case 'select':
                return (
                    <div className="flex flex-col gap-2" key={name}>
                        {label && (
                            <label htmlFor={name} className="block font-medium">
                                {label}{' '}
                                {required && (
                                    <span className="text-red-500">*</span>
                                )}
                            </label>
                        )}
                        <select
                            name={name}
                            id={name}
                            className="w-full rounded-xl border border-cloud-300 bg-cloud-200 px-4 py-3 font-sans text-base"
                            defaultValue={''}
                            required={requiredWithDefault}
                            onChange={handleChange}
                        >
                            <option value="" disabled>
                                Vyberte vaši roli
                            </option>
                            {options!.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                );
            default:
                return (
                    <FormInput
                        key={name}
                        name={name}
                        label={label!}
                        required={requiredWithDefault}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values[name as keyof typeof values]}
                        type={type}
                        error={
                            errors[name as keyof typeof errors] &&
                            touched[name as keyof typeof touched]
                                ? errors[name as keyof typeof errors]
                                : null
                        }
                        placeholder={placeholder}
                        readonly={readonly}
                        disabled={disabled}
                    />
                );
        }
    };

    return renderForm;
}